<template>
    <div class="preview-pane">
        <csp-pdf-viewer
            ref="pdfViewer"
            :document-url="downloadUrl"
            page-mode="none"
            @page-rendered="onReRender"
            @pages-loaded="onPagesLoaded"
            @scale-changing="onReRender"/>
    </div>
</template>

<script>

import Container from '@services/Container';
import PdfViewer from '@components/file/PdfViewer';

const container = Container.getInstance();
const recordRepository = container.getRecordRepository();

/**
 * CSS class applied to every rectangle overlay element drawn inside the PDF.js iframe.
 * Used as a selector for targeted cleanup.
 */
const RECT_HIGHLIGHT_CLASS = 'klippa-rect-highlight';

/**
 * Milliseconds to wait after a resize event before redrawing rectangles.
 * PDF.js needs a moment to finish its own layout recalculation before
 * offsetWidth/offsetHeight reflect the new dimensions.
 */
const RESIZE_DEBOUNCE_MS = 100;

const HIGHLIGHT_PADDING = 0.004;

/**
 * CSS injected into the PDF.js iframe document so that .klippa-rect-highlight
 * elements are styled correctly. The iframe has its own CSS scope — the main
 * document's Webpack stylesheet does not reach it.
 *
 * Keep in sync with the .klippa-rect-highlight block in
 * resources/scss/components/record/klippa-data-application-modal.scss.
 */
const IFRAME_HIGHLIGHT_STYLES = `
.klippa-rect-highlight {
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border-width: 2.5px;
  border-style: solid;
}
.klippa-rect-highlight.default {
  background-color: rgba(249, 192, 1, 0.45);
  border-color: rgba(249, 192, 1, 1);
}
.klippa-rect-highlight.hover {
  background-color: rgba(255, 125, 0, 0.45);
  border-color: #ff7d00;
}
`;


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default {
    name: 'DataApplicationFormPreview',
    components: {
        'csp-pdf-viewer': PdfViewer,
    },
    props: {
        recordId: {
            type: Number,
            required: true,
        },
        fileId: {
            type: Number,
            required: true,
        },
        formFields: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: [],
    data() {
        return {
            isLoadingActiveDocumentPreview: false,
            resizeObserver: null,
            resizeDebounceTimer: null,
            observerInitialized: false,
            fieldRectangleMap: {},
        }
    },
    computed: {
        downloadUrl() {
            return recordRepository.buildFileDownloadUrl(this.recordId, this.fileId);
        },
    },
    beforeDestroy() {
        this._destroyResizeObserver();
    },
    methods: {

        // --- Public API ---------------------------------------------------

        setVisibilityByFieldIdentifier(fieldIdentifier, visible) {
            if (Object.prototype.hasOwnProperty.call(this.fieldRectangleMap, fieldIdentifier)) {
                this.fieldRectangleMap[fieldIdentifier].enabled = visible;
            }
            this._redrawRectangles();
        },

        setHoverEffectByFieldIdentifier(fieldIdentifier, hovering) {
            if (Object.prototype.hasOwnProperty.call(this.fieldRectangleMap, fieldIdentifier)) {
                this.fieldRectangleMap[fieldIdentifier].class = hovering ? 'hover' : 'default';
            }
            this._redrawRectangles();
        },

        // --- Internal drawing logic ---------------------------------------

        _convertFormFields() {
            const fieldRectangleMap = {};
            for (const subformId in this.formFields) {
                const subform = this.formFields[subformId];
                if (!subform || typeof subform !== 'object') {
                    continue;
                }
                if (Object.prototype.hasOwnProperty.call(subform, 'contextData')) { // We only handle subforms
                    continue;
                }
                for (const fieldId in subform) {
                    const field = subform[fieldId];
                    const contextData = field.contextData;

                    if (!Array.isArray(contextData.coordinates)) {
                        continue;
                    }

                    const rectangles = contextData.coordinates
                        .map(this._convertKlippaRectToInternal)
                        .filter((rect) => rect !== null);
                    fieldRectangleMap[contextData.fieldIdentifier] = {
                        rectangles: rectangles,
                        enabled: true,
                        class: 'default',
                    };
                }
            }
            this.fieldRectangleMap = fieldRectangleMap;
        },

        /**
         * Converts a Klippa bounding-box object into the internal normalised format
         * used by this component for drawing.
         *
         * Klippa coordinate system:
         *   - Values are relative to the page dimensions, range 0.0–1.0
         *   - Origin is top-left; y grows downward
         *   - Vertices are always axis-aligned and ordered:
         *     [top-left, top-right, bottom-right, bottom-left]
         *   - Page index is 0-based
         *   - File index is 0-based; this preview only supports 1 File at a time
         *
         * @param {{ file: number, page: number, vertices: number[][] }} klippaRect
         * @returns {{ pageNumber: number, left: number, top: number, width: number, height: number }|null}
         *   Returns null when the rectangle must be skipped (file !== 0 or malformed vertices).
         */
        _convertKlippaRectToInternal(klippaRect) {
            if (klippaRect.file !== 0) {
                // Only file index 0 is valid in this single-file viewer.
                return null;
            }

            if (!Array.isArray(klippaRect.vertices) || klippaRect.vertices.length !== 4) {
                return null;
            }

            const x1 = klippaRect.vertices[0][0]; // top-left x
            const y1 = klippaRect.vertices[0][1]; // top-left y
            const x2 = klippaRect.vertices[2][0]; // bottom-right x
            const y2 = klippaRect.vertices[2][1]; // bottom-right y

            return {
                pageNumber: klippaRect.page + 1, // PDF.js uses 1-based page numbers
                left: x1 - HIGHLIGHT_PADDING,
                top: y1 - HIGHLIGHT_PADDING,
                width: x2 - x1 + HIGHLIGHT_PADDING * 2,
                height: y2 - y1 + HIGHLIGHT_PADDING * 2,
            };
        },

        /**
         * Returns the contentDocument of the PDF.js iframe, or null when the
         * viewer is not yet ready (pages not loaded, iframe not mounted).
         *
         * @returns {Document|null}
         */
        _getIframeDoc() {
            if (!this.$refs.pdfViewer) {
                return null;
            }
            const iframeWindow = this.$refs.pdfViewer.getIFrameContent();
            if (!iframeWindow || !iframeWindow.document) {
                return null;
            }
            return iframeWindow.document;
        },

        /**
         * Injects the highlight stylesheet into the PDF.js iframe document once.
         * The iframe has its own CSS scope — it does not inherit the main document's
         * Webpack stylesheet, so .klippa-rect-highlight would be unstyled without this.
         */
        _injectIframeStyles() {
            const iframeDoc = this._getIframeDoc();

            const STYLE_ID = 'klippa-highlight-styles';
            if (!iframeDoc || iframeDoc.getElementById(STYLE_ID)) {
                return;
            }
            const style = iframeDoc.createElement('style');
            style.id = STYLE_ID;
            style.textContent = IFRAME_HIGHLIGHT_STYLES;
            iframeDoc.head.appendChild(style);
        },

        /**
         * Removes all previously drawn rectangle overlays from the iframe document.
         *
         * @param {Document} iframeDoc
         */
        _removeAllRectangles(iframeDoc) {
            const existing = iframeDoc.getElementsByClassName(RECT_HIGHLIGHT_CLASS);
            // getElementsByClassName returns a live HTMLCollection — iterate backwards
            // to safely remove elements while iterating.
            for (let i = existing.length - 1; i >= 0; i--) {
                existing[i].remove();
            }
        },

        /**
         * Draws a single normalised rectangle onto a PDF.js page container.
         *
         * Pixel coordinates are computed from the canvas element inside .canvasWrapper,
         * not from pageDiv.offsetWidth/offsetHeight. PDF.js centres the canvas inside
         * the .page container and may round its pixel dimensions differently from the
         * container's CSS layout size. Using the canvas as both the size reference and
         * the positioning parent eliminates the resulting sub-pixel drift on zoom.
         *
         * @param {Element}  pageDiv  The .page element for the target page
         * @param {{ pageNumber: number, left: number, top: number, width: number, height: number }} rect
         * @param {string} cssClass
         */
        _drawRectangleOnPage(pageDiv, rect, cssClass) {
            const canvasWrapper = pageDiv.querySelector('.canvasWrapper');
            if (!canvasWrapper) {
                return;
            }
            const canvas = canvasWrapper.querySelector('canvas');
            if (!canvas) {
                return;
            }

            const pageWidth = canvas.offsetWidth;
            const pageHeight = canvas.offsetHeight;
            const el = pageDiv.ownerDocument.createElement('div');
            el.classList.add(RECT_HIGHLIGHT_CLASS);
            el.classList.add(cssClass);
            el.style.left = (rect.left * pageWidth) + 'px';
            el.style.top = (rect.top * pageHeight) + 'px';
            el.style.width = (rect.width * pageWidth) + 'px';
            el.style.height = (rect.height * pageHeight) + 'px';

            canvasWrapper.appendChild(el);
        },

        /**
         * Clears all existing overlays and redraws the current set of rectangles.
         *
         * Called after drawRectangles(), after pages-loaded, and after each resize.
         * Silently does nothing when the iframe is not yet ready.
         */
        _redrawRectangles() {
            const iframeDoc = this._getIframeDoc();
            if (!iframeDoc) {
                return;
            }

            this._removeAllRectangles(iframeDoc);

            if (Object.keys(this.fieldRectangleMap).length === 0) {
                return;
            }

            for (const fieldId in this.fieldRectangleMap) {
                const fieldRectangleConfig = this.fieldRectangleMap[fieldId];
                if (!fieldRectangleConfig.enabled) {
                    continue;
                }

                fieldRectangleConfig.rectangles.forEach((rect) => {
                    // Find the PDF.js page container for this rectangle's page number.
                    // PDF.js marks each page wrapper with data-page-number (1-based).
                    let targetPageDiv = null;
                    const pages = iframeDoc.getElementsByClassName('page');
                    for (let i = 0; i < pages.length; i++) {
                        if (parseInt(pages[i].getAttribute('data-page-number'), 10) === rect.pageNumber) {
                            targetPageDiv = pages[i];
                            break;
                        }
                    }

                    if (!targetPageDiv) {
                        // Page index out of range or pages not yet in DOM — skip silently.
                        return;
                    }

                    this._drawRectangleOnPage(targetPageDiv, rect, fieldRectangleConfig.class);
                });
            }
        },

        // --- ResizeObserver -----------------------------------------------

        /**
         * Registers a ResizeObserver on the iframe element.
         * Called once after the PDF pages have loaded.
         * Subsequent calls are no-ops (guarded by _observerInitialized).
         */
        _initResizeObserver() {
            if (this.observerInitialized) {
                return;
            }
            if (!this.$refs.pdfViewer || !this.$refs.pdfViewer.$el) {
                return;
            }

            this.resizeObserver = new ResizeObserver(() => {
                clearTimeout(this.resizeDebounceTimer);
                this.resizeDebounceTimer = setTimeout(() => {
                    this._redrawRectangles();
                }, RESIZE_DEBOUNCE_MS);
            });

            this.resizeObserver.observe(this.$refs.pdfViewer.$el);
            this.observerInitialized = true;
        },

        /**
         * Disconnects the ResizeObserver and clears any pending debounce timer.
         * Called in beforeDestroy().
         */
        _destroyResizeObserver() {
            clearTimeout(this.resizeDebounceTimer);
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
        },

        // --- PdfViewer event handler --------------------------------------

        /**
         * Called by csp-pdf-viewer once all PDF pages have been rendered.
         * Initialises the ResizeObserver (once) and redraws any pending rectangles.
         */
        onPagesLoaded() {
            this._injectIframeStyles();
            this._convertFormFields();
            this._initResizeObserver();
            this._redrawRectangles();
        },

        /**
         * Called by csp-pdf-viewer when PDF.js rerenders the canvas (scalechanging).
         */
        onReRender() {
            const iframeDoc = this._getIframeDoc();
            if (iframeDoc) {
                this._removeAllRectangles(iframeDoc);
            }
            clearTimeout(this.resizeDebounceTimer);
            this.resizeDebounceTimer = setTimeout(() => {
                this._redrawRectangles();
            }, RESIZE_DEBOUNCE_MS);
        },
    },
}

</script>
